import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import OTPVerificationComponent from '../../Common/OTPVerification/OTPVerificationComponent';
import Lottie from 'lottie-react';
import successLottie from '../../../utils/Lottie/NewSuccess.json';
import { toast } from 'react-toastify';
import GlassModal from './../../../v2/MUI_custom_component/Modal/GlassModal';
import { useDispatch, useSelector } from 'react-redux';
import { encryptPassword } from '../../../v2/Utils/Encryption/NodeForgeEncryption';
import { OTP_PURPOSE_ENUM } from '../../Auth/Enum';
import { sendOTPAction, verifyOTPAction } from '../../Auth/Register/API/Action';
import { CustomButton, TextField } from '../../../v2/MUI_custom_component';
import AnimationModal from '../../../v2/MUI_custom_component/Modal/AnimationModal';
import { updateNewMobileNumberAction } from './API/Action';

const ChangePhone = ({ open, onClose, onSuccess }) => {
    const [step, setStep] = useState('verifyCurrentPhone'); // 'verifyCurrentPhone', 'enterPhone', 'verifyNewPhone', 'success'
    const [newPhone, setNewPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const currentPhone = useSelector((state) => state.loginReducer.loginUser?.phone_no);
    const userId = useSelector((state) => state.loginReducer.loginUser?.user_id);
    // const encryptedMobile = encryptPassword(currentPhone);
    const VFC_TOKEN = useSelector(state => state.authReducer.VFC_TOKEN);
    const encryptedUserId = encryptPassword(String(userId));
    const dispatch = useDispatch();

    const getModalTitle = () => {
        switch (step) {
            case 'verifyCurrentPhone':
                return 'Enter OTP';
            case 'enterPhone':
                return 'Enter new mobile number';
            case 'verifyNewPhone':
                return 'Enter OTP';
        }
    };
    useEffect(() => {
        if (open && step === 'verifyCurrentPhone' && currentPhone) {
            handleResendOTP();
        }
    }, [open, step, currentPhone]);
    const handleResendOTP = async (mobile_number) => {
        const payload = {
            mobile_number: encryptPassword(mobile_number || currentPhone),
            purpose: step === 'verifyCurrentPhone' ? OTP_PURPOSE_ENUM.CHANGE_NUMBER : OTP_PURPOSE_ENUM.CHANGE_NUMBER_NEW,
            user_id: encryptedUserId,
            ...(step === 'enterPhone' && { vfc_token: VFC_TOKEN }),
        };
        const { error, message } = await sendOTPAction(payload);
        
        if (!error) {
            return { success: true, message };
        } else {
            toast.error(message || 'Failed to send OTP');
            return { success: false, message };
        }
    };
    const handleVerifyOTP = async (otp, mobileNumber) => {
        const payload = {
            mobile_number: typeof mobileNumber === 'string' && mobileNumber.includes('encrypted') 
                ? mobileNumber 
                : encryptPassword(mobileNumber),
            otp: encryptPassword(otp),
            purpose: step === 'verifyCurrentPhone' ? OTP_PURPOSE_ENUM.CHANGE_NUMBER : OTP_PURPOSE_ENUM.CHANGE_NUMBER_NEW,
            user_id: encryptedUserId,
        };
        const { data, error, message } = await verifyOTPAction(payload);
        if (!error) {
            dispatch({ type: 'SET_VFC_TOKEN', payload: data.vfc_token });
            await updateNewMobileNumberAction({
                mobile_number: typeof mobileNumber === 'string' && mobileNumber.includes('encrypted') 
                ? mobileNumber 
                : encryptPassword(mobileNumber),
                vfc_token: data.vfc_token,
            });            
            return { success: true, message, data };
        } else {
            toast.error(message || 'Failed to verify OTP');
            return { success: false, message };
        }
    };
    const handleCurrentPhoneSuccess = () => {
        setStep('enterPhone');
    };

    const handleOTPSuccess = (result) => {
        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            onSuccess?.(newPhone);
            onClose();
            setStep('verifyCurrentPhone');
            setNewPhone('');
        }, 2000);
    };
    const handleModalClose = () => {
        setStep('verifyCurrentPhone');
        setNewPhone('');
        onClose();
    };
    const handleSendOTPForNewPhone = async () => {
        const result = await handleResendOTP(newPhone);
        if (result.success) {
            setStep('verifyNewPhone');
        } else {
            toast.error(result.message || 'Failed to send OTP');
        }
    };
    const renderEnterPhoneStep = () => (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
        }}>
            <TextField
                fullWidth
                label="New Mobile Number*"
                value={newPhone}
                onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 10) {
                        setNewPhone(value);
                    }
                }}
                placeholder="Enter new Mobile number"
                inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                }}
                error={newPhone.length > 0 && newPhone.length !== 10}
                helperText={ newPhone.length > 0 && newPhone.length !== 10 ? 'Mobile number must be 10 digits' : '' }
            />
            <CustomButton
                variant="filled"
                fullWidth
                onClick={handleSendOTPForNewPhone}
                disabled={loading || newPhone.length !== 10}
                label={loading ? 'Sending OTP...' : 'Send OTP'}
            />
        </Box>
    );

    return (
        <>
            <GlassModal
                open={open}
                onClose={handleModalClose}
                title={getModalTitle()}
                scrollable={true}
                maxWidth="500px"
                zIndex={2}
                pt={4}
                maxHeight = '60%'
                topDesktop = '50%'
                topMobile = '60%'
            >
                {step === 'verifyCurrentPhone' && (
                    <OTPVerificationComponent
                        mobileNumber={currentPhone}
                        onVerify={(otp) => handleVerifyOTP(otp, currentPhone)}
                        onResend={() => handleResendOTP(currentPhone)}
                        purpose="verifyCurrentPhone"
                        customMessage={`Please enter OTP sent to existing mobile number ${currentPhone}`}
                        onSuccess={handleCurrentPhoneSuccess}
                        onError={(error) => {
                            console.error('Current phone OTP verification error:', error);
                        }}
                    />
                )}
                
                {step === 'enterPhone' && renderEnterPhoneStep()}
                
                {step === 'verifyNewPhone' && (
                    <OTPVerificationComponent
                        mobileNumber={newPhone}
                        onVerify={(otp) => handleVerifyOTP(otp, newPhone)}
                        onResend={() => handleResendOTP(newPhone)}
                        purpose="changePhone"
                        customMessage={`Please enter OTP sent to new mobile number ${newPhone}`}
                        onSuccess={handleOTPSuccess}
                        onError={(error) => {
                            console.error('New phone OTP verification error:', error);
                        }}
                    />
                )}
            </GlassModal>
            {showSuccess && (
                <AnimationModal
                    open={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    title='Successful!'
                    subtitle='Mobile number changed successfully.'
                    // onAnimationComplete={handleAnimationComplete}
                    loop={false}
                    animationData={successLottie}
                />
            )}
        </>
    );
}

export default ChangePhone;